/**
 * Quiz Popup Stub Component
 * 
 * Manages the quiz popup/modal and dynamically loads quiz content via Sections API.
 * Works with the quiz-popup section and quiz-content section.
 */

import { Component } from '@theme/component';
import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';
import { DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class QuizPopupStub extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.quizUrl = this.dataset.quizUrl;
    this.colorScheme = this.dataset.colorScheme || 'scheme-1';
    this.preloaded = false;
    // Support both data-dynamic-content and direct content rendering
    this.dynamicContent = this.querySelector('[data-dynamic-content]') || this.querySelector('.quiz-modal__content');
    this.dialogComponent = this.querySelector('dialog-component');
    this.originalQuizHTML = null; // Store original quiz HTML for restart
    this.quizHTMLBeforeEmail = null; // Store quiz HTML before showing email capture (for back button)
    this.isEmailSubmitting = false; // Flag to prevent double submission on email form
    this.originalBackButtonHTML = null; // Store original back button HTML for restart
    
    if (!this.dynamicContent) {
      console.error('Quiz popup stub missing content container');
      return;
    }
    
    // Check if content is already rendered (not using URL-based loading)
    if (!this.quizUrl && this.dynamicContent.querySelector('quiz-component')) {
      this.preloaded = true;
      // Store original HTML for restart functionality
      this.originalQuizHTML = this.dynamicContent.innerHTML;
      this.initializeQuizComponent();
    }
    
    // Listen for dialog close events to hide the stub
    // Note: We show the stub directly in initializePopup() when opening
    const handleDialogClose = () => {
      this.style.display = 'none';
    };
    
    document.addEventListener(DialogCloseEvent.eventName, handleDialogClose);
    this.handleDialogClose = handleDialogClose;
    
    this.setupTriggers();
  }

  /**
   * Setup trigger buttons that open this quiz popup
   */
  setupTriggers() {
    // Find all trigger buttons
    const triggers = document.querySelectorAll('[data-quiz-trigger]');
    
    triggers.forEach((trigger) => {
      // Skip if already initialized
      if (trigger.dataset.quizTriggerInitialized) {
        return;
      }
      
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.initializePopup(trigger);
      });
      
      // Preload on hover/focus for better UX
      trigger.addEventListener('mouseenter', () => {
        this.preload();
      });
      trigger.addEventListener('focus', () => {
        this.preload();
      });
      
      trigger.dataset.quizTriggerInitialized = 'true';
    });
  }

  /**
   * Preload quiz content before user opens the popup
   */
  async preload() {
    if (this.preloaded) return;
    
    // If content is already rendered, mark as preloaded
    if (!this.quizUrl && this.dynamicContent.querySelector('quiz-component')) {
      this.preloaded = true;
      this.initializeQuizComponent();
      return;
    }
    
    // Otherwise load from URL if provided
    if (this.quizUrl) {
    await this.getQuizContent();
    this.preloaded = true;
    }
  }

  /**
   * Initialize and open the popup
   */
  async initializePopup(opener) {
    // Ensure dialog component is ready
    if (!this.dialogComponent) {
      this.dialogComponent = this.querySelector('dialog-component');
    }
    
    if (!this.dialogComponent) {
      console.error('Quiz popup: dialog-component not found');
      return;
    }
    
    // Wait for dialog component to be fully initialized
    const dialogComponent = /** @type {any} */ (this.dialogComponent);
    if (!dialogComponent.refs || !dialogComponent.refs.dialog) {
      // Wait a bit for the component to initialize
      await new Promise(resolve => setTimeout(resolve, 10));
      if (!dialogComponent.refs || !dialogComponent.refs.dialog) {
        console.error('Quiz popup: dialog-component not ready');
        return;
      }
    }
    
    // If content is already rendered, just open the dialog
    if (this.preloaded && this.dynamicContent && this.dynamicContent.querySelector('quiz-component')) {
      this.initializeQuizComponent();
    } else if (this.quizUrl) {
      // Load content from URL if provided
    await this.getQuizContent();
    } else {
      // Content should already be rendered, just ensure it's initialized
      this.preloaded = true;
      this.initializeQuizComponent();
    }
    
    if (typeof dialogComponent.showDialog === 'function') {
      // Show the quiz popup stub before opening dialog
      this.style.display = 'block';
      dialogComponent.showDialog();
    } else {
      console.error('Quiz popup: showDialog method not available');
    }
  }

  /**
   * Initialize quiz component if it exists
   */
  initializeQuizComponent() {
    // Store original back button HTML if not already stored
    if (!this.originalBackButtonHTML) {
      const backButton = document.querySelector('[data-quiz-back]');
      if (backButton) {
        this.originalBackButtonHTML = backButton.innerHTML;
      }
    }
    
    const quizComponent = this.dynamicContent.querySelector('quiz-component');
    if (quizComponent) {
      // Listen for quiz completion - show results with email capture at top
      quizComponent.addEventListener('quiz:complete', async (event) => {
        const { result, selections, title } = event.detail;
        
        // Remove loading state from submit button
        const submitButton = quizComponent.querySelector('[data-quiz-submit]');
        if (submitButton) {
          submitButton.removeAttribute('aria-busy');
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          if (submitButton.dataset.originalText) {
            submitButton.textContent = submitButton.dataset.originalText;
          }
        }
        
        if (result) {
          if (result.url && this.quizUrl) {
            // Load result from URL if using URL-based loading
            await this.getQuizResult(result.url, title, selections);
          } else {
            // Render result directly from data with email capture at top
            await this.renderQuizResult(result, title, selections);
          }
        }
      });
      
      // Listen for no results
      quizComponent.addEventListener('quiz:no-results', () => {
        // Remove loading state from submit button
        const submitButton = quizComponent.querySelector('[data-quiz-submit]');
        if (submitButton) {
          submitButton.removeAttribute('aria-busy');
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          if (submitButton.dataset.originalText) {
            submitButton.textContent = submitButton.dataset.originalText;
          }
        }
        
        this.renderNoResults();
      });
      
      // Initialize if not already initialized, or re-initialize if HTML was restored
      const quizComponentAny = /** @type {any} */ (quizComponent);
      if (typeof quizComponentAny.connectedCallback === 'function') {
        // If component was already initialized but HTML was restored, re-initialize
        if (quizComponentAny.dataset?.initialized === 'true') {
          // Re-initialize to reload result data from script tag
          if (typeof quizComponentAny.initializeQuiz === 'function') {
            quizComponentAny.initializeQuiz();
          } else if (typeof quizComponentAny.loadResultData === 'function') {
            // Try to reload result data if method exists
            quizComponentAny.loadResultData();
          } else {
            // Fallback: call connectedCallback again
            quizComponentAny.connectedCallback();
          }
        } else {
          // First time initialization
          quizComponentAny.connectedCallback();
          quizComponentAny.dataset.initialized = 'true';
        }
      }
    }
  }

  /**
   * Render email capture step before showing results
   */
  async renderEmailCapture(result, selections, title) {
    const emailHeading = this.dataset.emailHeading || 'Your results are almost ready...';
    const emailMessage = this.dataset.emailMessage || 'We will send your personalized Lux Unfiltered routine straight to your inbox - plus 10 percent off your first order.';
    const skipButtonText = this.dataset.skipButtonText || 'SKIP';
    const klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
    const klaviyoListId = this.dataset.klaviyoListId || '';
    
    // Preload products in the background while showing email form
    const productsPromise = result.products ? this.preloadProducts(result.products) : Promise.resolve([]);
    
    // Render inline email form (no need to fetch via API - avoids 404 errors)
    this.dynamicContent.innerHTML = `
      <div class="quiz-modal__email-capture">
        <h2 class="quiz-modal__email-heading">${this.escapeHtml(emailHeading)}</h2>
        <p class="quiz-modal__email-message">${this.escapeHtml(emailMessage)}</p>
        <form class="quiz-modal__email-form" id="quiz-email-form" data-quiz-email-form novalidate>
          <label for="quiz-email-input" class="visually-hidden">Email (optional)</label>
          <input
            type="email"
            id="quiz-email-input"
            name="email"
            class="quiz-modal__email-input"
            placeholder="EMAIL (OPTIONAL)"
            autocomplete="email"
            autocorrect="off"
            autocapitalize="off"
          >
          <button
            type="submit"
            class="quiz-modal__button quiz-modal__button--email-submit"
            data-quiz-email-submit
          >
            ${this.escapeHtml(skipButtonText)}
          </button>
          <div class="quiz-modal__email-error" role="alert" hidden></div>
        </form>
      </div>
    `;
    
    // Store result data for after email submission
    this.pendingResult = { result, selections, title, productsPromise };
    
    // Show back button and setup handler
    const backButton = document.querySelector('[data-quiz-back]');
    if (backButton && backButton instanceof HTMLElement) {
      backButton.style.display = 'flex';
      
      // Remove any existing listeners by cloning and replacing to prevent duplicates
      const backButtonAny = /** @type {any} */ (backButton);
      if (backButtonAny.parentNode) {
        const newBackButton = backButton.cloneNode(true);
        backButtonAny.parentNode.replaceChild(newBackButton, backButton);
        
        // Add click handler to go back to quiz
        newBackButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goBackToQuiz();
          return false;
        });
      } else {
        // If no parent, just add listener directly
        backButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.goBackToQuiz();
          return false;
        });
      }
    }
    
    // Setup email form handler
    const emailForm = this.dynamicContent.querySelector('[data-quiz-email-form]');
    const emailInput = this.dynamicContent.querySelector('#quiz-email-input');
    const emailSubmitButton = this.dynamicContent.querySelector('[data-quiz-email-submit]');
    const errorElement = this.dynamicContent.querySelector('.quiz-modal__email-error');
    
    // Reset submission flag when email form is rendered
    this.isEmailSubmitting = false;
    
    if (emailForm && emailInput && emailSubmitButton) {
      // Store original button text
      const originalButtonText = emailSubmitButton.textContent.trim();
      
      // Update button text based on email input
      emailInput.addEventListener('input', (e) => {
        if (this.isEmailSubmitting) return; // Don't update if submitting
        
        const email = e.target.value.trim();
        if (email) {
          emailSubmitButton.textContent = 'CONTINUE';
        } else {
          emailSubmitButton.textContent = skipButtonText;
        }
        // Hide error when user types
        if (errorElement) {
          errorElement.hidden = true;
        }
      });
      
      // Handle button click directly to prevent multiple clicks
      emailSubmitButton.addEventListener('click', (e) => {
        if (this.isEmailSubmitting) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      });
      
      // Handle form submission - submit to Klaviyo with custom properties
      emailForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent double submission
        if (this.isEmailSubmitting) {
          return;
        }
        this.isEmailSubmitting = true;
        
        const email = emailInput.value.trim();
        
        // Set loading state immediately
        emailSubmitButton.setAttribute('aria-busy', 'true');
        emailSubmitButton.setAttribute('disabled', '');
        emailSubmitButton.classList.add('loading');
        emailSubmitButton.innerHTML = '<span class="quiz-modal__button-loading"></span>';
        
        try {
          // Submit email to Klaviyo if provided (don't block on failure)
          if (email) {
            try {
              await this.submitQuizEmailToKlaviyo(email, selections, klaviyoPublicKey, klaviyoListId);
            } catch (error) {
              // Log but don't block - proceed to results anyway
              console.warn('Email submission failed, proceeding to results:', error.message);
            }
          }
          
          // Wait for products to finish loading
          await productsPromise;
          
          // Show results
          if (this.pendingResult) {
            this.renderQuizResult(this.pendingResult.result, this.pendingResult.title);
            this.pendingResult = null;
          }
          
          // Clear stored quiz HTML since we're moving to results
          this.quizHTMLBeforeEmail = null;
        } catch (error) {
          console.error('Error processing email submission:', error);
          // Reset button state on error
          this.isEmailSubmitting = false;
          emailSubmitButton.removeAttribute('aria-busy');
          emailSubmitButton.removeAttribute('disabled');
          emailSubmitButton.classList.remove('loading');
          emailSubmitButton.textContent = originalButtonText;
        }
      });
    }
  }
  
  /**
   * Submit email to Klaviyo with quiz selections as custom properties
   * This method is non-blocking - errors are logged but don't prevent quiz completion
   */
  async submitQuizEmailToKlaviyo(email, selections, publicKey, listId) {
    // Build custom properties object from quiz selections
    const customProperties = {};
    customProperties.quiz_completed = true;
    customProperties.quiz_selections = selections.join(', ');
    customProperties.quiz_selection_count = selections.length;
    customProperties.quiz_completed_at = new Date().toISOString();
    
    // Add individual selections as properties
    selections.forEach((selection, index) => {
      const key = 'quiz_selection_' + (index + 1);
      customProperties[key] = selection;
    });
    
    // Use Klaviyo's client-side API if available
    if (typeof window !== 'undefined' && window.klaviyo && typeof window.klaviyo.push === 'function') {
      try {
        // Subscribe using Klaviyo client-side API
        window.klaviyo.push(['subscribe', {
          email: email,
          properties: customProperties
        }]);
        
        // Also identify the profile with custom properties for better tracking
        window.klaviyo.push(['identify', {
          email: email,
          properties: customProperties
        }]);
        
        return;
      } catch (error) {
        console.warn('Klaviyo push failed:', error);
      }
    }
    
    // If Klaviyo not available or not configured, just log and continue
    // Don't throw - we don't want to block the quiz results
    console.warn('Klaviyo not available or not configured - email not submitted');
  }
  
  /**
   * Product data cache to avoid redundant fetches
   * @type {Map<string, object>}
   */
  productCache = new Map();

  /**
   * Fetch a single product's data with caching
   * @param {string} handle - Product handle
   * @returns {Promise<object|null>} Product data or null on failure
   */
  async fetchProductData(handle) {
    if (!handle) return null;
    
    // Return cached data if available
    if (this.productCache.has(handle)) {
      return this.productCache.get(handle);
    }
    
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (response.ok) {
        const productData = await response.json();
        this.productCache.set(handle, productData);
        return productData;
      }
    } catch (error) {
      // Silently fail - product will render without extra data
    }
    return null;
  }

  /**
   * Batch fetch multiple products in parallel
   * @param {Array<{handle?: string}>} products - Array of products with handles
   * @returns {Promise<Map<string, object>>} Map of handle -> product data
   */
  async batchFetchProducts(products) {
    const handles = products
      .map(p => p.handle)
      .filter(Boolean)
      .filter(handle => !this.productCache.has(handle)); // Only fetch uncached
    
    if (handles.length === 0) {
      return this.productCache;
    }
    
    // Fetch all products in parallel
    const fetchPromises = handles.map(handle => 
      this.fetchProductData(handle).then(data => ({ handle, data }))
    );
    
    await Promise.all(fetchPromises);
    return this.productCache;
  }

  /**
   * Preload product data in the background (parallel fetching)
   * @param {Array<object>} products - Array of product objects
   * @returns {Promise<Array<object>>} Products with preloaded data attached
   */
  async preloadProducts(products) {
    // Batch fetch all products in parallel
    await this.batchFetchProducts(products);
    
    // Return products with cached data attached
    return products.map(product => {
      if (product.handle && this.productCache.has(product.handle)) {
        return { ...product, preloaded: this.productCache.get(product.handle) };
      }
      return product;
    });
  }

  /**
   * Render quiz result directly from result data
   * @param {object} result - Quiz result object
   * @param {string} title - Result title
   * @param {Array} selections - User's quiz selections (for email capture)
   */
  async renderQuizResult(result, title, selections = []) {
    if (!result) {
      this.dynamicContent.innerHTML = `
        <div class="quiz-error">
          <p>Error displaying quiz result. Please try again.</p>
        </div>
      `;
      return;
    }

    const resultsHeading = this.dataset.resultsHeading || 'Your Perfect Match';
    const products = result.products || [];
    const emailHeading = this.dataset.emailHeading || 'See Your Results in Your Inbox';
    const emailMessage = this.dataset.emailMessage || 'By entering your email, you agree to receive your quiz results and occasional marketing emails from us. You can unsubscribe at any time.';
    const emailSuccessHeading = this.dataset.emailSuccessHeading || 'Thank You!';
    const emailSuccessMessage = this.dataset.emailSuccessMessage || 'Your email has been successfully submitted. Check your inbox for your quiz results.';
    const emailErrorMessage = this.dataset.emailErrorMessage || 'There was an error submitting your email. Please try again.';
    const skipButtonText = this.dataset.skipButtonText || 'SUBMIT';
    const klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
    const klaviyoListId = this.dataset.klaviyoListId || '';
    const addAllToCartText = this.dataset.addAllToCartText || 'Add All to Bag';

    // Build email capture HTML (shown at top of results)
    const emailCaptureHTML = `
      <div class="quiz-modal__email-capture quiz-modal__email-capture--inline">
        <div class="quiz-modal__email-capture-content">
          <h2 class="quiz-modal__email-heading">${this.escapeHtml(emailHeading)}</h2>
          <p class="quiz-modal__email-message">${this.escapeHtml(emailMessage)}</p>
        </div>
        <div class="quiz-modal__email-capture-form-wrapper">
          <form class="quiz-modal__email-form" id="quiz-email-form" data-quiz-email-form novalidate>
            <label for="quiz-email-input" class="visually-hidden">Email</label>
            <input
              type="email"
              id="quiz-email-input"
              name="email"
              class="quiz-modal__email-input"
              placeholder="EMAIL*"
              autocomplete="email"
              autocorrect="off"
              autocapitalize="off"
            >
            <button
              type="submit"
              class="quiz-modal__button quiz-modal__button--email-submit"
              data-quiz-email-submit
            >
              ${this.escapeHtml(skipButtonText)}
            </button>
            <div class="quiz-modal__email-error" role="alert" hidden></div>
          </form>
        </div>
      </div>
    `;

    // Build header HTML with "Add all to cart" button
    const headerHTML = `
      <div class="quiz-modal__results-header">
        <div class="quiz-modal__results-header-top">
          <h2 class="quiz-modal__results-title">${this.escapeHtml(resultsHeading)}</h2>
          ${products.length > 1 ? `
            <button
              type="button"
              class="quiz-modal__button quiz-modal__button--add-all"
              data-quiz-add-all
            >
              ${this.escapeHtml(addAllToCartText)}
            </button>
          ` : ''}
        </div>
      </div>
    `;

    // Build products slider HTML
    let productsHTML = '';
    if (products.length > 0) {
      // Batch fetch all product data in parallel FIRST
      await this.batchFetchProducts(products);
      
      // Then render all cards (data is now cached, no more fetching)
      const productCards = products.map(product => this.renderProductCard(product));
      
      productsHTML = `
        <div class="quiz-modal__results-products-wrapper">
          <slideshow-component
            class="quiz-modal__results-slider"
            initial-slide="0"
            infinite="false"
          >
            <slideshow-container ref="slideshowContainer">
              <slideshow-slides
                class="quiz-modal__results-slides${products.length < 4 ? ' quiz-modal__results-slides--centered' : ''}"
                style="--slide-width: calc((100% - (var(--slideshow-gap, 20px) * ${Math.min(products.length, 4) - 1})) / ${Math.min(products.length, 4)}); --slideshow-gap: 20px;"
              >
                ${productCards.join('')}
              </slideshow-slides>
            </slideshow-container>
          </slideshow-component>
        </div>
      `;
    } else {
      productsHTML = '<p class="quiz-modal__results-empty">No products found for this result.</p>';
    }

    const resultHTML = `
      <quiz-result-component class="quiz-modal__results">
        ${emailCaptureHTML}
        ${headerHTML}
        ${productsHTML}
        ${products.length > 1 ? `
          <div class="quiz-modal__results-actions-mobile">
            <button
              type="button"
              class="quiz-modal__button quiz-modal__button--add-all quiz-modal__button--add-all-mobile"
              data-quiz-add-all-mobile
            >
              ${this.escapeHtml(addAllToCartText)}
            </button>
          </div>
        ` : ''}
      </quiz-result-component>
    `;

    this.dynamicContent.innerHTML = resultHTML;

    // Store selections for email submission
    this.pendingSelections = selections;

    // Setup email form handler
    this.setupEmailForm(klaviyoPublicKey, klaviyoListId);

    // Setup add to cart buttons
    this.setupAddToCartButtons();

    // Setup "Add all to cart" button
    if (products.length > 1) {
      this.setupAddAllToCartButton(products);
    }

    // Show retake button in header (where back button usually is)
    const backButton = document.querySelector('[data-quiz-back]');
    if (backButton) {
      backButton.style.display = 'flex';
      backButton.classList.add('quiz-modal__back--retake');
      backButton.classList.remove('hidden--mobile');
      backButton.innerHTML = `
        <span class="quiz-modal__back-text">Retake Quiz</span>
      `;
      // Remove existing listeners and add restart handler
      const newBackButton = backButton.cloneNode(true);
      backButton.parentNode.replaceChild(newBackButton, backButton);
      newBackButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuizRestart();
        backButton.classList.add('hidden--mobile');
        return false;
      });
    }
  }

  /**
   * Format price from cents to dollars
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string (e.g., "$29.99")
   */
  formatPrice(priceInCents) {
    return `$${(priceInCents / 100).toFixed(2)}`;
  }

  /**
   * Render product card with add to cart functionality
   * Uses cached product data - call batchFetchProducts() first
   * @param {object} product - Product object with handle, title, url, etc.
   * @returns {string} HTML string for the product card
   */
  renderProductCard(product) {
    const productTitle = product.title || 'Product';
    const productUrl = product.url || '#';
    const productImage = product.featured_image || '';
    const productHandle = product.handle || '';
    const productId = product.id || '';
    
    // Get price from cached data (already fetched in parallel)
    let productPrice = '';
    let variantId = '';
    if (productHandle && this.productCache.has(productHandle)) {
      const cachedData = this.productCache.get(productHandle);
      if (cachedData?.variants?.[0]) {
        productPrice = this.formatPrice(cachedData.variants[0].price);
        variantId = cachedData.variants[0].id;
      }
    }
    
    const imageHTML = productImage 
      ? `<img src="${productImage}" alt="${this.escapeHtml(productTitle)}" class="quiz-result-product-card__image" loading="lazy">`
      : '<div class="quiz-result-product-card__image quiz-result-product-card__image--placeholder"></div>';
    
    // If we have variant ID from cache, include it directly (no fetch needed on click)
    const addToCartButton = productHandle 
      ? `<form action="/cart/add" method="post" enctype="multipart/form-data" class="quiz-result-product-card__form">
          <input type="hidden" name="id" value="${variantId}" data-variant-id>
          <button type="submit" class="quiz-result-product-card__add-button" data-product-handle="${productHandle}" data-product-id="${productId}"${variantId ? '' : ' data-needs-variant'}>
            <span>Add to Cart</span>
            ${productPrice ? `<span class="quiz-result-product-card__price">${productPrice}</span>` : ''}
          </button>
        </form>`
      : `<a href="${productUrl}" class="quiz-result-product-card__view-link">View Product</a>`;
    
    return `
      <slideshow-slide class="quiz-result-product-card" data-product-handle="${productHandle}" data-product-id="${productId}">
        <div class="quiz-result-product-card__inner">
          <a href="${productUrl}" class="quiz-result-product-card__image-link">
            ${imageHTML}
          </a>
          <div class="quiz-result-product-card__content">
            <h3 class="quiz-result-product-card__title h3">
              <a href="${productUrl}">${this.escapeHtml(productTitle)}</a>
            </h3>
            ${addToCartButton}
          </div>
        </div>
      </slideshow-slide>
    `;
  }

  /**
   * Setup add to cart buttons for product cards
   * Uses AJAX to add to cart, opens cart drawer, and closes quiz popup
   */
  setupAddToCartButtons() {
    const addToCartButtons = this.dynamicContent.querySelectorAll('.quiz-result-product-card__add-button');
    
    addToCartButtons.forEach((button) => {
      const productHandle = button.dataset.productHandle;
      const productId = button.dataset.productId;
      const form = button.closest('form');
      
      if (!form || !productHandle) return;
      
      const variantInput = form.querySelector('[data-variant-id]');
      
      // Prevent default form submission - we'll handle it with AJAX
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent double submission
        if (button.hasAttribute('disabled') || button.classList.contains('loading')) {
          return;
        }
        
        let variantId = variantInput?.value;
        
        // If variant ID is not set, try to get it from cache or fetch it
        if (!variantId) {
          const cachedData = this.productCache.get(productHandle);
          if (cachedData?.variants?.[0]) {
            variantId = cachedData.variants[0].id;
          } else {
            // Fetch product data if not in cache
        try {
          const productData = await this.fetchProductData(productHandle);
          if (productData?.variants?.[0]) {
                variantId = productData.variants[0].id;
              } else {
                // Fallback: navigate to product page
                window.location.href = `/products/${productHandle}`;
                return;
          }
        } catch (error) {
              // Fallback: navigate to product page
          window.location.href = `/products/${productHandle}`;
              return;
            }
          }
        }
        
        // Store original button content (preserve price element)
        const priceElement = button.querySelector('.quiz-result-product-card__price');
        const priceHTML = priceElement ? priceElement.outerHTML : '';
        const originalText = button.querySelector('span:not(.quiz-result-product-card__price)')?.textContent || 'Add to Cart';
        const originalHTML = `<span>${originalText}</span>${priceHTML}`;
        
        // Set loading state
        button.setAttribute('disabled', '');
        button.setAttribute('aria-busy', 'true');
        button.classList.add('loading');
        button.innerHTML = '<span class="quiz-modal__button-loading"></span>';
        
        // Prepare form data
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        
        // Add sections for cart drawer update
        const cartDrawerComponent = document.querySelector('cart-drawer-component');
        const cartItemsComponent = document.querySelector('cart-items-component');
        if (cartItemsComponent?.dataset?.sectionId) {
          formData.append('sections', cartItemsComponent.dataset.sectionId);
        }
        
        const fetchCfg = fetchConfig('javascript', { body: formData });
        
        try {
          const response = await fetch(Theme.routes.cart_add_url, {
            ...fetchCfg,
            headers: {
              ...fetchCfg.headers,
              Accept: 'text/html',
            },
          });
          
          const responseData = await response.json();
          
          if (responseData.status) {
            // Error adding to cart
            button.removeAttribute('disabled');
            button.removeAttribute('aria-busy');
            button.classList.remove('loading');
            button.innerHTML = originalHTML;
            console.error('Error adding to cart:', responseData.message);
            return;
          }
          
          // Success - show added state
          button.classList.add('atc-added');
          button.classList.remove('loading');
          button.removeAttribute('aria-busy');
          const addedText = button.dataset.addedText || 'Added';
          button.innerHTML = `<span>${addedText}</span>`;
          
          // Dispatch CartAddEvent to trigger cart drawer
          document.dispatchEvent(
            new CartAddEvent({}, variantId.toString(), {
              source: 'quiz-popup-stub',
              itemCount: 1,
              productId: productId,
              sections: responseData.sections,
            })
          );
          
          // Close quiz popup dialog
          if (this.dialogComponent) {
            this.dialogComponent.closeDialog();
          }
          
          // Open cart drawer
          if (cartDrawerComponent && typeof cartDrawerComponent.open === 'function') {
            cartDrawerComponent.open();
          }
          
          // Reset button state after delay (only if dialog wasn't closed)
          setTimeout(() => {
            if (button.isConnected) {
              button.classList.remove('atc-added');
              button.removeAttribute('disabled');
              button.innerHTML = originalHTML;
            }
          }, 2000);
          
        } catch (error) {
          console.error('Error adding to cart:', error);
          button.removeAttribute('disabled');
          button.removeAttribute('aria-busy');
          button.classList.remove('loading');
          button.innerHTML = originalHTML;
        }
      });
    });
  }

  /**
   * Setup email form handler for inline email capture
   * @param {string} klaviyoPublicKey - Klaviyo public key
   * @param {string} klaviyoListId - Klaviyo list ID
   */
  setupEmailForm(klaviyoPublicKey, klaviyoListId) {
    const emailForm = this.dynamicContent.querySelector('[data-quiz-email-form]');
    const emailFormWrapper = this.dynamicContent.querySelector('.quiz-modal__email-capture-form-wrapper');
    const emailInput = this.dynamicContent.querySelector('#quiz-email-input');
    const emailSubmitButton = this.dynamicContent.querySelector('[data-quiz-email-submit]');
    const errorElement = this.dynamicContent.querySelector('.quiz-modal__email-error');
    const emailCaptureContent = this.dynamicContent.querySelector('.quiz-modal__email-capture-content');
    const emailErrorMessage = this.dataset.emailErrorMessage || 'There was an error submitting your email. Please try again.';
    
    if (!emailForm || !emailInput || !emailSubmitButton) {
      return; // Email form not found
    }
    
    // Reset submission flag
    this.isEmailSubmitting = false;
    
    // Store original button text
    const originalButtonText = emailSubmitButton.textContent.trim();
    const skipButtonText = this.dataset.skipButtonText || 'SUBMIT';
    
    // Update button text based on email input
    emailInput.addEventListener('input', (e) => {
      if (this.isEmailSubmitting) return;
      
      const email = e.target.value.trim();
      if (email) {
        emailSubmitButton.textContent = skipButtonText;
      } else {
        emailSubmitButton.textContent = skipButtonText;
      }
      // Hide error when user types
      if (errorElement) {
        errorElement.hidden = true;
      }
    });
    
    // Handle form submission
    emailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent double submission
      if (this.isEmailSubmitting) {
        return;
      }
      this.isEmailSubmitting = true;
      
      const email = emailInput.value.trim();
      
      // Validate email if provided
      if (email && !this.isValidEmail(email)) {
        this.isEmailSubmitting = false;
        if (errorElement) {
          errorElement.textContent = 'Please enter a valid email address';
          errorElement.hidden = false;
        }
        return;
      }
      
      // Set loading state
      emailSubmitButton.setAttribute('aria-busy', 'true');
      emailSubmitButton.setAttribute('disabled', '');
      emailSubmitButton.classList.add('loading');
      emailSubmitButton.innerHTML = '<span class="quiz-modal__button-loading"></span>';
      
      // Hide any previous errors
      if (errorElement) {
        errorElement.hidden = true;
      }
      
      try {
        // Submit email to Klaviyo if provided
        if (email) {
          try {
            await this.submitQuizEmailToKlaviyo(email, this.pendingSelections || [], klaviyoPublicKey, klaviyoListId);
            
            // Show success state - hide form wrapper (input and button), update heading and message
            if (emailFormWrapper) {
              emailFormWrapper.style.display = 'none';
            }
            if (emailForm) {
              emailForm.style.display = 'none';
            }
            // Update heading and message in content area to show success confirmation
            if (emailCaptureContent) {
              const heading = emailCaptureContent.querySelector('.quiz-modal__email-heading');
              const message = emailCaptureContent.querySelector('.quiz-modal__email-message');
              if (heading) {
                heading.textContent = this.dataset.emailSuccessHeading || 'Thank You!';
              }
              if (message) {
                message.textContent = this.dataset.emailSuccessMessage || 'Your email has been successfully submitted. Check your inbox for your quiz results.';
              }
            }
          } catch (error) {
            // Show error message below input
            console.warn('Email submission failed:', error.message);
            if (emailFormWrapper) {
              emailFormWrapper.style.display = 'block';
            }
            if (emailForm) {
              emailForm.style.display = 'flex';
            }
            if (errorElement) {
              errorElement.textContent = emailErrorMessage;
              errorElement.hidden = false;
            }
            // Remove loading state on error
            emailSubmitButton.removeAttribute('aria-busy');
            emailSubmitButton.removeAttribute('disabled');
            emailSubmitButton.classList.remove('loading');
            emailSubmitButton.textContent = originalButtonText;
            this.isEmailSubmitting = false;
            return;
          }
        } else {
          // No email provided - just hide the form wrapper
          if (emailFormWrapper) {
            emailFormWrapper.style.display = 'none';
          }
          if (emailForm) {
            emailForm.style.display = 'none';
          }
        }
        
        this.isEmailSubmitting = false;
      } catch (error) {
        console.error('Error processing email submission:', error);
        this.isEmailSubmitting = false;
        emailSubmitButton.removeAttribute('aria-busy');
        emailSubmitButton.removeAttribute('disabled');
        emailSubmitButton.classList.remove('loading');
        emailSubmitButton.textContent = originalButtonText;
      }
    });
  }
  
  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} True if valid email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  
  /**
   * Setup "Add all to cart" button functionality
   * @param {Array} products - Array of product objects with handle and id
   */
  async setupAddAllToCartButton(products) {
    const addAllButton = this.dynamicContent.querySelector('[data-quiz-add-all]');
    const addAllButtonMobile = this.dynamicContent.querySelector('[data-quiz-add-all-mobile]');
    
    if ((!addAllButton && !addAllButtonMobile) || !products || products.length === 0) {
      return;
    }
    
    // Shared handler function for both buttons
    const handleAddAllClick = async (e, button) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Get both buttons to update states together
      const buttons = [addAllButton, addAllButtonMobile].filter(Boolean);
      
      // Prevent double submission
      if (button.hasAttribute('disabled') || button.classList.contains('loading')) {
        return;
      }
      
      // Store original button content
      const originalText = button.textContent.trim();
      
      // Set loading state on all buttons
      buttons.forEach(btn => {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('loading');
        btn.innerHTML = '<span class="quiz-modal__button-loading"></span>';
      });
      
      try {
        // Fetch all product data to get variant IDs
        const productDataPromises = products.map(product => {
          if (product.handle) {
            return this.fetchProductData(product.handle);
          }
          return Promise.resolve(null);
        });
        
        const productDataArray = await Promise.all(productDataPromises);
        
        // Collect all variant IDs (using first variant of each product)
        const variantIds = [];
        for (let i = 0; i < productDataArray.length; i++) {
          const productData = productDataArray[i];
          if (productData?.variants?.[0]?.id) {
            variantIds.push({
              id: productData.variants[0].id.toString(),
              quantity: 1
            });
          }
        }
        
        if (variantIds.length === 0) {
          throw new Error('No products available to add to cart');
        }
        
        // Add items to cart one by one (more reliable than bulk add)
        const cartItemsComponent = document.querySelector('cart-items-component');
        const sectionId = cartItemsComponent?.dataset?.sectionId || '';
        let lastResponseData = null;
        let successCount = 0;
        
        for (const item of variantIds) {
          try {
            const formData = new FormData();
            formData.append('id', item.id);
            formData.append('quantity', item.quantity.toString());
            
            if (sectionId) {
              formData.append('sections', sectionId);
            }
            
            const fetchCfg = fetchConfig('javascript', { body: formData });
            
            const response = await fetch(Theme.routes.cart_add_url, {
              ...fetchCfg,
              headers: {
                ...fetchCfg.headers,
                Accept: 'text/html',
              },
            });
            
            const responseData = await response.json();
            
            if (responseData.status) {
              console.warn(`Error adding variant ${item.id} to cart:`, responseData.message);
              continue; // Continue with next item
            }
            
            lastResponseData = responseData;
            successCount++;
            
            // Dispatch CartAddEvent for each successful add
            document.dispatchEvent(
              new CartAddEvent({}, item.id, {
                source: 'quiz-popup-stub',
                itemCount: item.quantity,
                sections: responseData.sections,
              })
            );
          } catch (error) {
            console.warn(`Error adding variant ${item.id} to cart:`, error);
            continue; // Continue with next item
          }
        }
        
        if (successCount === 0) {
          // No items were added successfully
          buttons.forEach(btn => {
            btn.removeAttribute('disabled');
            btn.removeAttribute('aria-busy');
            btn.classList.remove('loading');
            btn.textContent = originalText;
          });
          console.error('Failed to add any items to cart');
          return;
        }
        
        // Success - show added state on all buttons
        const successText = successCount === variantIds.length ? 'Added to Bag' : `Added ${successCount} of ${variantIds.length}`;
        buttons.forEach(btn => {
          btn.classList.add('atc-added');
          btn.classList.remove('loading');
          btn.removeAttribute('aria-busy');
          btn.textContent = successText;
        });
        
        // Open cart drawer
        const cartDrawerComponent = document.querySelector('cart-drawer-component');
        if (cartDrawerComponent && typeof cartDrawerComponent.open === 'function') {
          cartDrawerComponent.open();
        }
        
        // Close quiz popup dialog
        if (this.dialogComponent) {
          this.dialogComponent.closeDialog();
        }
        
        // Reset button state after delay
        setTimeout(() => {
          buttons.forEach(btn => {
            if (btn.isConnected) {
              btn.classList.remove('atc-added');
              btn.removeAttribute('disabled');
              btn.textContent = originalText;
            }
          });
        }, 2000);
        
      } catch (error) {
        console.error('Error adding all items to cart:', error);
        buttons.forEach(btn => {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-busy');
          btn.classList.remove('loading');
          btn.textContent = originalText;
        });
      }
    };
    
    // Attach handler to desktop button
    if (addAllButton) {
      addAllButton.addEventListener('click', (e) => handleAddAllClick(e, addAllButton));
    }
    
    // Attach handler to mobile button
    if (addAllButtonMobile) {
      addAllButtonMobile.addEventListener('click', (e) => handleAddAllClick(e, addAllButtonMobile));
    }
  }

  /**
   * Go back to quiz from email capture page
   */
  goBackToQuiz() {
    // Clear pending result since we're going back
    this.pendingResult = null;
    
    // Restore quiz HTML if we have it stored
    if (this.quizHTMLBeforeEmail) {
      this.dynamicContent.innerHTML = this.quizHTMLBeforeEmail;
      this.quizHTMLBeforeEmail = null; // Clear stored HTML
      
      // Re-initialize quiz component
      this.initializeQuizComponent();
      
      // Hide back button (quiz component will manage its visibility)
      const backButton = document.querySelector('[data-quiz-back]');
      if (backButton && backButton instanceof HTMLElement) {
        backButton.style.display = 'none';
      }
    } else if (this.originalQuizHTML) {
      // Fallback: restore original quiz HTML
      this.dynamicContent.innerHTML = this.originalQuizHTML;
      this.preloaded = true;
      this.initializeQuizComponent();
      
      // Hide back button
      const backButton = document.querySelector('[data-quiz-back]');
      if (backButton && backButton instanceof HTMLElement) {
        backButton.style.display = 'none';
      }
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Handle quiz restart - resets to initial state
   * Consolidated handler to avoid duplicate code
   */
  handleQuizRestart() {
    // Reset back button to normal state (remove retake styling and restore original HTML)
    const backButton = document.querySelector('[data-quiz-back]');
    if (backButton) {
      backButton.classList.remove('quiz-modal__back--retake');
      // Restore original back button HTML
      if (this.originalBackButtonHTML) {
        backButton.innerHTML = this.originalBackButtonHTML;
      }
    }
    
    // Restore original quiz HTML
    if (this.originalQuizHTML) {
      this.dynamicContent.innerHTML = this.originalQuizHTML;
      this.preloaded = true;
      // Show back button again (will be hidden/shown by quiz component based on question)
      if (backButton) {
        backButton.style.display = '';
      }
      // Re-initialize the quiz component
      this.initializeQuizComponent();
    } else if (this.quizUrl) {
      // If using URL-based loading, reload content
      this.preloaded = false;
      this.getQuizContent();
    } else {
      // Fallback: reload the page section
      location.reload();
    }
  }

  /**
   * Setup restart button click handler
   * @param {HTMLElement} container - Container element to search for restart button
   */
  setupRestartButton(container) {
    const restartButton = container.querySelector('[data-quiz-restart]');
    if (restartButton) {
      restartButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuizRestart();
        return false;
      });
    }
  }

  /**
   * Render no results page when no matching quiz results are found
   */
  renderNoResults() {
    const noResultsTitle = this.dataset.noResultsTitle || 'No Results Found';
    const noResultsMessage = this.dataset.noResultsMessage || 'We could not find a perfect match for your selections. Browse our collection instead.';
    const noResultsButtonText = this.dataset.noResultsButtonText || 'Browse Collection';
    const noResultsLink = this.dataset.noResultsLink || '/collections/all';

    const noResultsHTML = `
      <div class="quiz-modal__no-results">
        <h2 class="quiz-modal__no-results-title">${this.escapeHtml(noResultsTitle)}</h2>
        <p class="quiz-modal__no-results-message">${this.escapeHtml(noResultsMessage)}</p>
        <a href="${noResultsLink}" class="quiz-modal__button quiz-modal__button--default-link">
          ${this.escapeHtml(noResultsButtonText)}
        </a>
        <div class="quiz-modal__results-actions">
          <button type="button" class="quiz-modal__button-link quiz-modal__button-link--retake" data-quiz-restart>
            Retake Quiz
          </button>
        </div>
      </div>
    `;

    this.dynamicContent.innerHTML = noResultsHTML;

    // Hide back button on no results page
    const backButton = document.querySelector('[data-quiz-back]');
    if (backButton) {
      backButton.style.display = 'none';
    }

    // Handle restart button
    this.setupRestartButton(this.dynamicContent);
  }

  /**
   * Fetch quiz content via Sections API
   */
  async getQuizContent() {
    if (this.preloaded && this.dynamicContent.innerHTML.trim() !== '') return;
    
    try {
      // Build URL for section rendering
      const url = new URL(this.quizUrl, window.location.origin);
      
      // Get section HTML using sectionRenderer
      const sectionId = 'quiz-content';
      const html = await sectionRenderer.getSectionHTML(
        sectionId,
        false, // Don't use cache for dynamic content
        url
      );
      
      if (!html) {
        throw new Error(`Quiz content not found for URL: ${this.quizUrl}`);
      }
      
      this.dynamicContent.innerHTML = html;
      this.preloaded = true;
      
      // Initialize quiz component
      this.initializeQuizComponent();
    } catch (error) {
      console.error('Error loading quiz content:', error);
      this.dynamicContent.innerHTML = `
        <div class="quiz-error">
          <p>Error loading quiz. Please try again.</p>
        </div>
      `;
    }
  }

  /**
   * Load quiz result page after quiz completion
   * @param {string} resultUrl - URL to load result from
   * @param {string} title - Result title
   * @param {Array} selections - User's quiz selections (for email capture)
   */
  async getQuizResult(resultUrl, title, selections = []) {
    this.preloaded = false;
    
    try {
      // Build URL for section rendering
      const url = new URL(resultUrl, window.location.origin);
      
      // Get section HTML using sectionRenderer
      const sectionId = 'quiz-result';
      const html = await sectionRenderer.getSectionHTML(
        sectionId,
        false, // Don't use cache for dynamic content
        url
      );
      
      if (!html) {
        throw new Error(`Quiz result not found for URL: ${resultUrl}`);
      }
      
      this.dynamicContent.innerHTML = html;
      
      // Store selections for email submission
      this.pendingSelections = selections;
      
      // Setup email form if it exists
      const klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
      const klaviyoListId = this.dataset.klaviyoListId || '';
      this.setupEmailForm(klaviyoPublicKey, klaviyoListId);
      
      // Setup "Add all to cart" button if products exist
      const resultComponent = this.dynamicContent.querySelector('quiz-result-component');
      if (resultComponent) {
        const products = Array.from(resultComponent.querySelectorAll('[data-product-handle]'));
        if (products.length > 0) {
          const productData = products.map(product => ({
            handle: product.dataset.productHandle,
            id: product.dataset.productId
          }));
          this.setupAddAllToCartButton(productData);
        }
        
        // Handle restart button
        this.setupRestartButton(resultComponent);
        
        // Initialize if not already initialized
        if (typeof resultComponent.connectedCallback === 'function' && !resultComponent.dataset.initialized) {
          resultComponent.connectedCallback();
          resultComponent.dataset.initialized = 'true';
        }
      }
    } catch (error) {
      console.error('Error loading quiz result:', error);
      this.dynamicContent.innerHTML = `
        <div class="quiz-error">
          <p>Error loading quiz result. Please try again.</p>
        </div>
      `;
    }
  }
}

// Register the custom element
if (!customElements.get('quiz-popup-stub')) {
  customElements.define('quiz-popup-stub', QuizPopupStub);
}

